import { useRef, useState } from "react";
import { Box, MenuItem, MenuList, Popover, Typography } from "@mui/material";
import { DotLegalEmptyState, DotLegalSearchField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { fieldMenuItemSx, hintSx } from "./calculatedField.styles.ts";

interface FormulaFieldOptionItem {
    id: string;
    name: string;
    typeName: string;
}

interface FormulaFieldPopoverProps {
    anchorEl: HTMLElement | null;
    open: boolean;
    options: Array<FormulaFieldOptionItem>;
    onSelect: (fieldId: string) => void;
    onClose: () => void;
}

function FormulaFieldPopover(props: FormulaFieldPopoverProps) {
    return (
        <Popover
            anchorEl={props.anchorEl}
            open={props.open}
            onClose={props.onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{ paper: { sx: { width: 320, maxHeight: 400, display: "flex", flexDirection: "column", marginTop: 0.5 } } }}
        >
            {/* The popover unmounts its content when closed, so the search starts empty every time it opens */}
            <FormulaFieldList options={props.options} onSelect={props.onSelect} />
        </Popover>
    );
}

function FormulaFieldList(props: { options: Array<FormulaFieldOptionItem>; onSelect: (fieldId: string) => void }) {
    const { translateString } = useTranslation();
    const [searchText, setSearchText] = useState("");
    const menuListRef = useRef<HTMLUListElement>(null);

    const search = searchText.toLowerCase();
    const filteredOptions = props.options.filter((option) => option.name.toLowerCase().includes(search));

    const focusFirstOption = (event: React.KeyboardEvent) => {
        if (event.key === "ArrowDown") {
            event.preventDefault();
            menuListRef.current?.querySelector<HTMLElement>("[role='menuitem']")?.focus();
        }
    };

    return (
        <>
            <Box sx={{ padding: 1 }} onKeyDown={focusFirstOption}>
                <DotLegalSearchField
                    labelSearch={translateString("search")}
                    value={searchText}
                    debounce={false}
                    autoFocus
                    onChange={setSearchText}
                    useFullWidth
                />
            </Box>
            <Box sx={{ overflowY: "auto", flexGrow: 1, scrollbarWidth: "thin" }}>
                {filteredOptions.length === 0 ? (
                    <DotLegalEmptyState text={translateString("noResultsTryNewSearchWord")} noFixedHeight />
                ) : (
                    <>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", color: "text.secondary", paddingX: 2, paddingTop: 0.5 }}>
                            {translateString("formulaFieldsHeader")}
                        </Typography>
                        <MenuList ref={menuListRef} autoFocusItem={false}>
                            {filteredOptions.map((option) => (
                                <MenuItem key={option.id} onClick={() => props.onSelect(option.id)} sx={fieldMenuItemSx}>
                                    <span>{option.name}</span>
                                    <Box component="span" sx={hintSx}>
                                        {option.typeName}
                                    </Box>
                                </MenuItem>
                            ))}
                        </MenuList>
                    </>
                )}
            </Box>
        </>
    );
}

export default FormulaFieldPopover;
